import React, { useMemo, useEffect, useRef } from "react";
import Header from "../components/Header/Header";
import Footer from "../components/Footer/Footer";
import HeroSection from "./HeroSection/HeroSection";
import { BodySection } from "./HeroSection/HeroSectionStyled";
import { useLocation } from "react-router-dom";
import { ourServicesConstant } from "../components/OurServices/constants";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const Services = () => {
  const location = useLocation();
  const bodyRef = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const featuresRef = useRef<HTMLDivElement>(null);

  const serviceData = useMemo(() => {
    const data = ourServicesConstant.find(
      (item) => item.id.toString() === location?.pathname.split("/")[2]
    );
    return data;
  }, [location]);

  useEffect(() => {
    const body = bodyRef.current;
    const content = contentRef.current;
    const sidebar = sidebarRef.current;
    const features = featuresRef.current;

    if (!body || !content) return;

    const ctx = gsap.context(() => {
      // Main content animation
      gsap.fromTo(
        content.querySelector(".sectionLabel"),
        { opacity: 0, x: -30 },
        {
          opacity: 1,
          x: 0,
          duration: 0.6,
          ease: "power3.out",
          scrollTrigger: {
            trigger: content,
            start: "top 80%",
            toggleActions: "play none none reverse",
          },
        }
      );

      gsap.fromTo(
        content.querySelector("h2"),
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 0.7,
          ease: "power3.out",
          delay: 0.1,
          scrollTrigger: {
            trigger: content,
            start: "top 80%",
            toggleActions: "play none none reverse",
          },
        }
      );

      gsap.fromTo(
        content.querySelector(".leadParagraph"),
        { opacity: 0, x: -30 },
        {
          opacity: 1,
          x: 0,
          duration: 0.8,
          ease: "power3.out",
          delay: 0.2,
          scrollTrigger: {
            trigger: content,
            start: "top 80%",
            toggleActions: "play none none reverse",
          },
        }
      );

      gsap.fromTo(
        content.querySelector(".contentParagraph"),
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 0.7,
          ease: "power3.out",
          scrollTrigger: {
            trigger: content.querySelector(".contentParagraph"),
            start: "top 85%",
            toggleActions: "play none none reverse",
          },
        }
      );

      // Sidebar animation
      if (sidebar) {
        gsap.fromTo(
          sidebar.querySelector(".imageCard"),
          { opacity: 0, x: 30, scale: 0.95 },
          {
            opacity: 1,
            x: 0,
            scale: 1,
            duration: 0.8,
            ease: "power3.out",
            scrollTrigger: {
              trigger: sidebar,
              start: "top 80%",
              toggleActions: "play none none reverse",
            },
          }
        );

        gsap.fromTo(
          sidebar.querySelector(".ctaCard"),
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 0.7,
            ease: "power3.out",
            delay: 0.2,
            scrollTrigger: {
              trigger: sidebar,
              start: "top 80%",
              toggleActions: "play none none reverse",
            },
          }
        );
      }

      // Features grid animation
      if (features) {
        gsap.fromTo(
          features.querySelectorAll(".featureCard"),
          { opacity: 0, y: 40, scale: 0.95 },
          {
            opacity: 1,
            y: 0,
            scale: 1,
            duration: 0.6,
            stagger: 0.1,
            ease: "power3.out",
            scrollTrigger: {
              trigger: features,
              start: "top 85%",
              toggleActions: "play none none reverse",
            },
          }
        );
      }
    }, body);

    return () => ctx.revert();
  }, [serviceData]);

  return (
    <>
      <Header />
      <HeroSection
        title={serviceData?.title}
        description={serviceData?.firstParagraph}
      />
      <BodySection ref={bodyRef}>
        <div className="bodySectionContainer">
          <div className="contentGrid">
            <div className="mainContent" ref={contentRef}>
              <div className="sectionLabel">
                <span className="line" />
                <span>About This Service</span>
              </div>

              <h2>
                Why Choose Our{" "}
                <span className="highlight">{serviceData?.title}</span>
              </h2>

              <p className="leadParagraph">{serviceData?.firstParagraph}</p>

              <p className="contentParagraph">{serviceData?.descripiton}</p>
            </div>

            <div className="sidebar" ref={sidebarRef}>
              <div className="imageCard">
                <img
                  src={serviceData?.image}
                  alt={serviceData?.title || "service"}
                />
              </div>

              <div className="ctaCard">
                <h3>Need This Service?</h3>
                <p>
                  Get in touch with our team to discuss your requirements and
                  get a free consultation.
                </p>
                <a href="/#contactUs" className="ctaButton">
                  Contact Us
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </a>
              </div>
            </div>
          </div>

          <div className="featuresGrid" ref={featuresRef}>
            <div className="featureCard">
              <div className="featureIcon">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
              </div>
              <h4>Quality Assured</h4>
              <p>
                We maintain the highest standards of quality in all our services
                with rigorous testing and verification.
              </p>
            </div>

            <div className="featureCard">
              <div className="featureIcon">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
              </div>
              <h4>Quick Turnaround</h4>
              <p>
                Fast and efficient service delivery to minimize downtime and
                keep your business running smoothly.
              </p>
            </div>

            <div className="featureCard">
              <div className="featureIcon">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </div>
              <h4>Expert Team</h4>
              <p>
                Our certified professionals bring years of experience and
                expertise to every project.
              </p>
            </div>
          </div>
        </div>
      </BodySection>
      <Footer />
    </>
  );
};

export default Services;
